import React from "react";
import { Card } from "./TvBits";

const M = (v) => { const a = Math.abs(v || 0); const s = v < 0 ? "-" : ""; return a >= 1e9 ? `${s}$${(a / 1e9).toFixed(2)}B` : a >= 1e6 ? `${s}$${(a / 1e6).toFixed(1)}M` : `${s}$${Math.round(a / 1e3)}k`; };
const G = "#10F5A0", R = "#FF3B6B", C = "#00F0FF", A = "#F5C451";

const Stat = ({ k, v, color, sub, testid }) => (
  <div className="nx-hud rounded-2xl px-5 py-3 relative overflow-hidden" data-testid={testid}>
    <p className="nx-mono text-[11px] tracking-[0.3em] text-cyan-200/50">{k}</p>
    <p className="nx-orbitron font-black text-[clamp(1.4rem,2.2vw,2.5rem)] leading-tight" style={{ color, textShadow: `0 0 18px ${color}55` }}>{v}</p>
    {sub && <p className="nx-mono text-xs text-cyan-100/50">{sub}</p>}
    <span className="absolute -right-6 -bottom-6 w-20 h-20 rounded-full blur-2xl" style={{ background: `${color}22` }} />
  </div>
);

const TideChart = ({ s }) => {
  if (!s.length) return null;
  const W = 900, H = 260, vals = s.flatMap((x) => [x.c, x.p]);
  const lo = Math.min(0, ...vals), hi = Math.max(0, ...vals), span = hi - lo || 1;
  const x = (i) => (i / Math.max(1, s.length - 1)) * W, y = (v) => H - ((v - lo) / span) * H;
  const path = (k) => s.map((p, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(p[k]).toFixed(1)}`).join(" ");
  const area = (k) => `${path(k)} L${W},${y(0)} L0,${y(0)} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-full" preserveAspectRatio="none" data-testid="tv-whale-tide-chart">
      <defs>
        <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={G} stopOpacity="0.35" /><stop offset="1" stopColor={G} stopOpacity="0" /></linearGradient>
        <linearGradient id="gp" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={R} stopOpacity="0.35" /><stop offset="1" stopColor={R} stopOpacity="0" /></linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((f) => <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="#00F0FF14" strokeDasharray="4 6" />)}
      <line x1="0" x2={W} y1={y(0)} y2={y(0)} stroke="#ffffff33" />
      <path d={area("c")} fill="url(#gc)" /><path d={area("p")} fill="url(#gp)" />
      <path d={path("c")} fill="none" stroke={G} strokeWidth="2.5" style={{ filter: `drop-shadow(0 0 4px ${G})` }} />
      <path d={path("p")} fill="none" stroke={R} strokeWidth="2.5" style={{ filter: `drop-shadow(0 0 4px ${R})` }} />
    </svg>
  );
};

const Heat = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => Math.max(r.bull, r.bear)));
  return (
    <div className="flex flex-col gap-[0.35em]" data-testid="tv-whale-heat">
      {rows.map((r) => (
        <div key={r.ticker} className="grid grid-cols-[1fr_5.5rem_1fr] items-center gap-2">
          <div className="flex justify-end"><div className="h-4 rounded-l" style={{ width: `${(r.bear / max) * 100}%`, background: `linear-gradient(270deg, ${R}, ${R}33)`, boxShadow: `0 0 8px ${R}66` }} /></div>
          <p className="nx-orbitron text-center font-black text-cyan-50 text-base">{r.ticker}<span className="block nx-mono text-[10px] text-cyan-200/40 font-normal">{r.n} alerts</span></p>
          <div><div className="h-4 rounded-r" style={{ width: `${(r.bull / max) * 100}%`, background: `linear-gradient(90deg, ${G}, ${G}33)`, boxShadow: `0 0 8px ${G}66` }} /></div>
        </div>
      ))}
    </div>
  );
};

const Sweeps = ({ rows }) => (
  <div className="flex flex-col gap-[0.3em]" data-testid="tv-whale-sweeps">
    <div className="grid grid-cols-[4.5rem_3.2rem_4.5rem_6.5rem_5.5rem_4rem_3.5rem] gap-2 nx-mono text-[10px] tracking-widest text-cyan-200/40 px-2">
      <span>TICKER</span><span>SIDE</span><span>STRIKE</span><span>EXPIRY</span><span>PREMIUM</span><span>ASK%</span><span>V/OI</span>
    </div>
    {rows.map((r, i) => (
      <div key={i} className="grid grid-cols-[4.5rem_3.2rem_4.5rem_6.5rem_5.5rem_4rem_3.5rem] gap-2 items-center px-2 py-[0.25em] rounded-lg border border-white/5 bg-white/[0.02]" data-testid={`tv-whale-sweep-${i}`}>
        <b className="nx-orbitron text-cyan-50">{r.ticker}</b>
        <span className="nx-mono font-bold" style={{ color: r.type === "CALL" ? G : R }}>{r.type}</span>
        <span className="nx-mono text-cyan-100">{r.strike}</span>
        <span className="nx-mono text-cyan-100/60 text-sm">{r.expiry}</span>
        <span className="nx-mono font-black" style={{ color: A }}>{M(r.premium)}</span>
        <span className="nx-mono text-sm" style={{ color: r.ask_pct >= 60 ? G : r.ask_pct <= 40 ? R : "#94A3B8" }}>{r.ask_pct}%</span>
        <span className="nx-mono text-sm text-cyan-100/70">{r.vol_oi}</span>
      </div>
    ))}
  </div>
);

const Dark = ({ rows }) => {
  const max = Math.max(1, ...rows.map((r) => r.premium));
  return (
    <div className="flex flex-col gap-[0.35em]" data-testid="tv-whale-darkpool">
      {rows.map((r) => (
        <div key={r.ticker} className="grid grid-cols-[4.5rem_1fr_5.5rem_5rem] items-center gap-2">
          <b className="nx-orbitron text-cyan-50">{r.ticker}</b>
          <div className="h-3.5 rounded bg-white/5 overflow-hidden"><div className="h-full rounded" style={{ width: `${(r.premium / max) * 100}%`, background: `linear-gradient(90deg, #7DD3FC, ${C})`, boxShadow: `0 0 10px ${C}88` }} /></div>
          <span className="nx-mono font-black text-cyan-100 text-right">{M(r.premium)}</span>
          <span className="nx-mono text-xs text-cyan-200/50 text-right">{r.prints} prints @{r.px?.toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
};

export const TvWhaleFlow = ({ d }) => {
  const u = d.uw_flow;
  if (!u) return null;
  const t = u.tide || {}, tot = u.totals || {};
  const bull = (t.net_call || 0) > (t.net_put || 0);
  const ratio = tot.put_prem ? (tot.call_prem / tot.put_prem).toFixed(2) : "—";
  return (
    <div className="h-full grid grid-rows-[auto_1fr_1fr] grid-cols-[1.15fr_1fr] gap-4" data-testid="tv-scene-whales">
      <div className="col-span-2 grid grid-cols-5 gap-4">
        <Stat k="MARKET TIDE" v={bull ? "BULLISH" : "BEARISH"} color={bull ? G : R} sub="net premium flow" testid="tv-whale-bias" />
        <Stat k="NET CALL PREM" v={M(t.net_call)} color={(t.net_call || 0) >= 0 ? G : R} testid="tv-whale-netcall" />
        <Stat k="NET PUT PREM" v={M(t.net_put)} color={(t.net_put || 0) >= 0 ? R : G} testid="tv-whale-netput" />
        <Stat k="CALL / PUT $" v={ratio} color={C} sub={`${M(tot.call_prem)} / ${M(tot.put_prem)}`} testid="tv-whale-ratio" />
        <Stat k="SWEEPS / ALERTS" v={`${tot.sweeps || 0} / ${tot.alerts || 0}`} color={A} sub={`updated ${new Date(u.ts * 1000).toLocaleTimeString()}`} testid="tv-whale-count" />
      </div>
      <Card title="MARKET TIDE · NET PREMIUM" accent={bull ? G : R} code="CALLS ▲ GREEN · PUTS ▼ RED">
        <TideChart s={u.tide_series || []} />
      </Card>
      <Card title="TOP SWEEPS" accent={A} code="UNUSUAL WHALES · LIVE" className="row-span-2">
        <Sweeps rows={u.sweeps || []} />
      </Card>
      <div className="grid grid-cols-2 gap-4 min-h-0">
        <Card title="FLOW HEAT" accent={C} code="BEAR ◀ ▶ BULL"><Heat rows={(u.heat || []).slice(0, 7)} /></Card>
        <Card title="DARK POOL" accent="#7DD3FC" code="OFF-EXCHANGE"><Dark rows={(u.darkpool || []).slice(0, 8)} /></Card>
      </div>
    </div>
  );
};
